import { createContext, useContext, useEffect, useRef } from "react";
import { io } from "socket.io-client";
// crypto.js exists in utils but encryption is not applied
import useAuthStore from "../store/authStore";
import useChatStore from "../store/chatStore";
import useNotification from "../hooks/useNotification";

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const socketRef  = useRef(null);
  const { user }   = useAuthStore();
  const { showNotification } = useNotification();

  // FIX 2: Store userId in a ref so the effect never re-runs due to object identity changes
  const userIdRef = useRef(user?._id);
  useEffect(() => { userIdRef.current = user?._id; }, [user?._id]);

  useEffect(() => {
    if (!user?._id) return;

    const userId = user._id;

    const socket = io(process.env.REACT_APP_SOCKET_URL || "http://localhost:5000", {
      withCredentials: true,
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 45000,
    });

    socketRef.current = socket;

    // Register read emitter
    useChatStore.getState().setEmitRead((conversationId) => {
      socket.emit("message:read", { conversationId, userId });
    });

    // FIX 3: Proper connect/disconnect/error handlers
    socket.on("connect", () => {
      console.log("✅ Socket connected:", socket.id);
      socket.emit("user:online", userId);
    });

    socket.on("connect_error", (err) => {
      console.warn("⚠️ Socket connect error:", err.message);
    });

    socket.on("disconnect", (reason) => {
      console.log("🔌 Socket disconnected:", reason);
      // FIX 3: If server kicked us, manually reconnect
      if (reason === "io server disconnect") {
        setTimeout(() => socket.connect(), 1000);
      }
    });

    socket.on("reconnect", (attempt) => {
      console.log(`🔄 Reconnected after ${attempt} attempt(s)`);
      // Re-register after reconnect
      socket.emit("user:online", userId);
    });

    socket.on("reconnect_error", (err) => {
      console.warn("⚠️ Reconnect error:", err.message);
    });

    // ── Online users ─────────────────────────────────────────────────────────
    socket.on("users:online", (userIds) => {
      useChatStore.getState().setOnlineUsers(userIds);
    });

    // ── Messages ─────────────────────────────────────────────────────────────
    socket.on("message:receive", async (message) => {
      useChatStore.getState().receiveMessage(message);

      if (message.sender?._id !== userId) {
        const state = useChatStore.getState();
        const isMuted  = (state.mutedConversations || []).includes(message.conversationId);
        const isActive = state.activeConversation?._id === message.conversationId;
        if (!isMuted && !isActive) {
          const body = message.isVoice ? "🎤 Voice message"
            : message.image  ? "📷 Photo"
            : message.gifUrl ? "🎬 GIF"
            : message.file   ? `📎 ${message.fileName || "File"}`
            : message.text   || "New message";
          // Show browser notification when tab not focused
          showNotification("Chatify — " + (message.sender?.name || "Someone"), body, message.sender?.avatar);
        }
      }
    });

    socket.on("message:edit", ({ messageId, text }) => {
      useChatStore.getState().updateMessageInState(messageId, { text, edited: true });
    });

    socket.on("message:delete", ({ messageId, isDeletedForEveryone }) => {
      useChatStore.getState().deleteMessageFromState(messageId, isDeletedForEveryone);
    });

    socket.on("message:read", ({ conversationId, userId: readerId }) => {
      useChatStore.getState().markConversationRead(conversationId, readerId);
    });

    socket.on("message:react", ({ messageId, reactions }) => {
      useChatStore.getState().updateMessageInState(messageId, { reactions });
    });

    socket.on("message:pin", ({ conversationId, pinnedMessages }) => {
      // Update both sidebar and activeConversation so PinBar re-renders instantly
      useChatStore.getState().updateConversationInState(conversationId, { pinnedMessages });
    });

    // ── Typing ────────────────────────────────────────────────────────────────
    socket.on("typing:start", ({ conversationId, userId: uid, userName }) => {
      useChatStore.getState().setTyping(conversationId, uid, userName, true);
    });

    socket.on("typing:stop", ({ conversationId, userId: uid }) => {
      useChatStore.getState().setTyping(conversationId, uid, "", false);
    });

    // ── Groups ────────────────────────────────────────────────────────────────
    socket.on("group:update", ({ conversation }) => {
      if (!conversation?._id) return;
      // Full replace — ensures groupAdmin, groupAdmins, participants all update instantly
      useChatStore.getState().updateConversationInState(conversation._id, conversation);
    });

    socket.on("group:admin_promoted", ({ conversationId, promotedBy, promotedUserId, promotedName, conversation }) => {
      // Full replace so admin badge appears immediately for all members
      if (conversation?._id) {
        useChatStore.getState().updateConversationInState(conversation._id, conversation);
      }

      // Show as system message in the chat (like WhatsApp) — no popup, no emoji
      const { activeConversation, messages } = useChatStore.getState();
      const isMe = promotedUserId === user._id;
      const text = isMe
        ? `${promotedBy} made you an admin`
        : `${promotedBy} made ${promotedName} an admin`;

      if (activeConversation?._id === conversationId) {
        const systemMsg = {
          _id: "sys_admin_" + Date.now(),
          conversationId,
          isSystem: true,
          text,
          createdAt: new Date().toISOString(),
          sender: { _id: "system", name: "system" },
          readBy: [],
        };
        useChatStore.setState({ messages: [...messages, systemMsg] });
      }
    });

    socket.on("group:leave", ({ conversationId, userId: leftId, userName, newAdminId, conversation: updatedConv }) => {
      const { activeConversation } = useChatStore.getState();

      if (updatedConv?._id) {
        // Full replace — admin, participants, groupAdmins all update at once
        useChatStore.getState().updateConversationInState(conversationId, updatedConv);
      } else {
        // Partial patch — just remove the member
        const state = useChatStore.getState();
        const conv  = state.activeConversation?._id === conversationId
          ? state.activeConversation
          : state.conversations.find((c) => c._id === conversationId);
        if (conv) {
          useChatStore.getState().updateConversationInState(conversationId, {
            participants: (conv.participants || []).filter((p) => (p._id || p) !== leftId),
          });
        }
      }

      // Add system message — always read fresh state (never stale closure)
      const freshState = useChatStore.getState();
      if (freshState.activeConversation?._id === conversationId && userName) {
        let msgText = `${userName} left the group`;
        if (newAdminId && updatedConv) {
          const newAdmin = updatedConv.participants?.find(
            (p) => (p._id || p)?.toString() === newAdminId?.toString()
          );
          if (newAdmin?.name) msgText = `${userName} left the group. ${newAdmin.name} is now the admin.`;
        }
        const systemMsg = {
          _id:           "sys_leave_" + Date.now(),
          conversationId,
          isSystem:      true,
          text:          msgText,
          createdAt:     new Date().toISOString(),
          sender:        { _id: leftId, name: userName },
          readBy:        [],
        };
        useChatStore.setState({ messages: [...freshState.messages, systemMsg] });
      }
    });

    socket.on("group:deleted", ({ conversationId }) => {
      // Remove the deleted group from all members' sidebar
      const { activeConversation } = useChatStore.getState();
      useChatStore.setState((state) => ({
        conversations: state.conversations.filter((c) => c._id !== conversationId),
        activeConversation: activeConversation?._id === conversationId ? null : state.activeConversation,
        messages: activeConversation?._id === conversationId ? [] : state.messages,
      }));
    });

    socket.on("group:member_joined", ({ conversationId, userName, conversation }) => {
      if (conversation) useChatStore.getState().updateConversationInState(conversationId, conversation);
      // Add as permanent system message in the chat
      if (userName) {
        const { activeConversation, messages } = useChatStore.getState();
        if (activeConversation?._id === conversationId) {
          const systemMsg = {
            _id: "sys_" + Date.now(),
            conversationId,
            isSystem: true,
            text: `${userName} was added to the group`,
            createdAt: new Date().toISOString(),
            sender: { _id: "system", name: "system" },
            readBy: [],
          };
          useChatStore.setState({ messages: [...messages, systemMsg] });
        }
      }
    });

    socket.on("conversation:new", (conversation) => {
      useChatStore.getState().addConversation(conversation);
    });

    // ── Status real-time ─────────────────────────────────────────────────────
    // These are handled in StatusList component directly via socket.on
    // Here we just ensure the socket is shared via context

    // Real-time bio/name/avatar update from contacts
    socket.on("profile:updated", ({ userId, name, bio, avatar }) => {
      const { conversations, activeConversation } = useChatStore.getState();
      // Update participant in all conversations
      const updatedConvs = conversations.map((conv) => ({
        ...conv,
        participants: conv.participants.map((p) =>
          p._id === userId ? { ...p, name, bio, avatar } : p
        ),
      }));
      useChatStore.setState({ conversations: updatedConvs });
      // Also update activeConversation if this person is in it
      if (activeConversation) {
        const updatedParticipants = activeConversation.participants.map((p) =>
          p._id === userId ? { ...p, name, bio, avatar } : p
        );
        useChatStore.setState({
          activeConversation: { ...activeConversation, participants: updatedParticipants },
        });
      }
    });

    return () => {
      socket.off(); // remove all listeners before disconnect
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?._id]); // only reconnect when the actual user ID changes

  return (
    <SocketContext.Provider value={socketRef}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
